<template>
  <el-container class="gas-layout">
    <el-aside width="232px" class="gas-aside">
      <nav class="gas-menu">
        <template v-for="group in displayMenus">
          <div v-if="!group.children || !group.children.length" :key="group.path" class="menu-group">
            <router-link :to="group.path" class="menu-item" :class="{ active: isActive(group.path) }">
              <i :class="group.meta.icon || 'el-icon-menu'"></i>
              <span>{{ group.meta.title }}</span>
            </router-link>
          </div>
          <div v-else :key="group.path" class="menu-group">
            <div
              class="menu-item menu-parent"
              :class="{ expanded: expanded[group.path], active: isGroupActive(group) }"
              @click="toggleGroup(group.path)"
            >
              <i :class="group.meta.icon || 'el-icon-folder'"></i>
              <span>{{ group.meta.title }}</span>
              <i class="el-icon-arrow-down arrow"></i>
            </div>
            <div v-show="expanded[group.path]" class="submenu">
              <router-link
                v-for="child in group.children"
                :key="menuIndex(group.path, child.path)"
                :to="menuIndex(group.path, child.path)"
                class="submenu-item"
                :class="{ active: $route.path === menuIndex(group.path, child.path) }"
              >
                <i v-if="child.meta.icon" :class="['submenu-icon', child.meta.icon]"></i>
                <i v-else class="submenu-icon submenu-icon-empty"></i>
                <span>{{ child.meta.title }}</span>
              </router-link>
            </div>
          </div>
        </template>
      </nav>
    </el-aside>
    <el-container class="gas-main-wrap">
      <el-header height="64px" class="gas-header">
        <div class="brand brand-link" title="返回首页概览" @click="goHomeOverview">
          <img :src="companyLogo" alt="logo" class="brand-logo" />
          <span class="brand-name">{{ companyName }}</span>
        </div>
        <div class="header-center">
          <span class="header-clock">{{ clockText }}</span>
          <span class="header-weather-divider">|</span>
          <span class="header-weather" :title="weatherTitle">
            <i
              v-if="weather.icon"
              :class="['weather-icon', weather.icon]"
              :style="{ color: weather.color }"
            ></i>
            <span class="weather-label">{{ weather.label }}</span>
            <span v-if="weather.temp != null" class="weather-temp">{{ weather.temp }}°C</span>
            <span v-else-if="!weatherLoading" class="weather-temp weather-temp--muted">--°C</span>
          </span>
        </div>
        <div class="header-right">
          <el-popover
            v-model="noticeVisible"
            placement="bottom-end"
            width="380"
            trigger="click"
            popper-class="gas-notice-popover"
            @show="onNoticeShow"
          >
            <header-notice-panel
              ref="noticePanel"
              :sound-muted="noticeSoundMuted"
              @count-change="handleNoticeCount"
              @navigate="noticeVisible = false"
              @sound-muted-change="setNoticeSoundMuted"
            />
            <el-badge
              slot="reference"
              :value="noticeCount"
              :max="99"
              :hidden="noticeCount <= 0"
              class="header-badge"
            >
              <i class="el-icon-bell header-icon" title="消息通知"></i>
            </el-badge>
          </el-popover>
          <i
            class="el-icon-question header-icon"
            title="在线帮助"
            @click="helpVisible = true"
          ></i>
          <theme-switcher />
          <header-help-dialog :visible.sync="helpVisible" />
          <el-dropdown trigger="click" @command="onUserCommand">
            <span class="user-block">
              <span class="user-avatar">管</span>
              <span>{{ userName }}</span>
              <i class="el-icon-arrow-down"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="changePassword">修改密码</el-dropdown-item>
              <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <header-change-password-dialog :visible.sync="changePasswordVisible" @success="onPasswordChanged" />
        </div>
      </el-header>
      <el-main class="gas-main">
        <router-view />
      </el-main>
    </el-container>
  </el-container>
</template>

<script>
import { sidebarMenus, menuIndex } from '@/config/menu'
import { normalizeDeviceRouteChildren } from '@/utils/menuNormalize'
import { resetRouter } from '@/router'
import { gasApi } from '@/api/gas'
import HeaderNoticePanel from '@/components/layout/HeaderNoticePanel.vue'
import HeaderHelpDialog from '@/components/layout/HeaderHelpDialog.vue'
import HeaderChangePasswordDialog from '@/components/layout/HeaderChangePasswordDialog.vue'
import ThemeSwitcher from '@/components/layout/ThemeSwitcher.vue'
import { fetchCurrentWeather } from '@/utils/headerWeather'
import { refreshSiteBranding } from '@/utils/siteBranding'

export default {
  components: { HeaderNoticePanel, HeaderHelpDialog, HeaderChangePasswordDialog, ThemeSwitcher },
  data() {
    return {
      clockText: '',
      expanded: {},
      timer: null,
      noticeVisible: false,
      noticeCount: 0,
      lastNoticeCount: null,
      noticeSoundMuted: false,
      noticeAudioContext: null,
      helpVisible: false,
      changePasswordVisible: false,
      noticePollTimer: null,
      weatherLoading: false,
      weather: {
        label: '晴',
        icon: 'el-icon-sunny',
        color: '#faad14',
        temp: null
      },
      weatherTimer: null
    }
  },
  computed: {
    userName() {
      return this.$store.state.name || '管理员'
    },
    companyName() {
      const b = this.$store.state.siteBranding
      return (b && b.companyName) || '上海晟颢信息科技有限公司'
    },
    companyLogo() {
      const b = this.$store.state.siteBranding
      return (b && b.companyLogo) || '/logo.png'
    },
    weatherTitle() {
      if (this.weatherLoading) return '天气加载中'
      const city = this.weather.city ? `${this.weather.city} · ` : ''
      return `${city}中央气象台实况`
    },
    displayMenus() {
      const dynamic = this.$store.state.routes || []
      if (dynamic.length) {
        return dynamic.map(r => ({
          path: r.path,
          meta: r.meta || {},
          children: normalizeDeviceRouteChildren(r.children).map(c => ({
            path: c.path,
            meta: c.meta || {},
            component: c.component
          }))
        }))
      }
      return sidebarMenus
    }
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler(path) {
        this.displayMenus.forEach(g => {
          if (g.children && g.children.some(c => path === menuIndex(g.path, c.path))) {
            this.$set(this.expanded, g.path, true)
          }
        })
      }
    }
  },
  mounted() {
    this.loadNoticeSoundSetting()
    this.tickClock()
    this.timer = setInterval(this.tickClock, 1000)
    sidebarMenus.forEach(g => {
      if (g.children) this.$set(this.expanded, g.path, g.path === '/home')
    })
    this.refreshNoticeCount()
    this.noticePollTimer = setInterval(this.refreshNoticeCount, 5000)
    this.loadWeather()
    this.weatherTimer = setInterval(this.loadWeather, 30 * 60 * 1000)
    refreshSiteBranding()
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
    if (this.noticePollTimer) clearInterval(this.noticePollTimer)
    if (this.weatherTimer) clearInterval(this.weatherTimer)
  },
  methods: {
    menuIndex,
    toggleGroup(path) {
      this.$set(this.expanded, path, !this.expanded[path])
    },
    isActive(path) {
      return this.$route.path === path
    },
    isGroupActive(group) {
      if (!group.children) return this.isActive(group.path)
      return group.children.some(c => this.$route.path === menuIndex(group.path, c.path))
    },
    tickClock() {
      const now = new Date()
      const pad = n => String(n).padStart(2, '0')
      this.clockText = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    },
    goHomeOverview() {
      if (this.$route.path !== '/home/overview') {
        this.$router.push('/home/overview')
      }
    },
    async loadWeather() {
      this.weatherLoading = true
      try {
        const w = await fetchCurrentWeather()
        this.weather = {
          label: w.label,
          icon: w.icon,
          color: w.color,
          temp: w.temp,
          city: w.city
        }
      } catch (e) {
        this.weather = {
          label: '晴',
          icon: 'el-icon-sunny',
          color: '#faad14',
          temp: this.weather.temp
        }
      } finally {
        this.weatherLoading = false
      }
    },
    onUserCommand(cmd) {
      if (cmd === 'changePassword') {
        this.changePasswordVisible = true
        return
      }
      if (cmd === 'logout') this.logout()
    },
    onPasswordChanged() {
      this.logout()
    },
    onNoticeShow() {
      this.$nextTick(() => {
        if (this.$refs.noticePanel) this.$refs.noticePanel.loadNotices()
      })
    },
    loadNoticeSoundSetting() {
      try {
        this.noticeSoundMuted = window.localStorage.getItem('gas_notice_sound_muted') === '1'
      } catch (e) {
        this.noticeSoundMuted = false
      }
    },
    setNoticeSoundMuted(val) {
      this.noticeSoundMuted = !!val
      try {
        window.localStorage.setItem('gas_notice_sound_muted', this.noticeSoundMuted ? '1' : '0')
      } catch (e) {
        // localStorage may be unavailable in restricted browser modes.
      }
      if (this.noticeSoundMuted && window.speechSynthesis) {
        window.speechSynthesis.cancel()
      }
    },
    refreshNoticeCount() {
      gasApi.alarm
        .list({ pageNum: 1, pageSize: 1, alarmStatus: 'active', keyword: '' })
        .then(res => {
          this.handleNoticeCount(Number((res.data && res.data.total) || 0))
          if (this.noticeVisible && this.$refs.noticePanel) this.$refs.noticePanel.loadNotices()
        })
        .catch(() => {
          this.handleNoticeCount(0)
        })
    },
    handleNoticeCount(count) {
      const next = Number(count || 0)
      if (this.lastNoticeCount !== null && next > this.lastNoticeCount) {
        this.playAlarmNotice(next - this.lastNoticeCount)
      }
      this.noticeCount = next
      this.lastNoticeCount = next
    },
    playAlarmNotice(delta) {
      if (this.noticeSoundMuted) return
      this.ringAlarmBell()
      this.speakAlarmNotice(delta)
    },
    ringAlarmBell() {
      if (typeof window === 'undefined') return
      const AudioContext = window.AudioContext || window.webkitAudioContext
      if (!AudioContext) return
      try {
        const ctx = this.noticeAudioContext || new AudioContext()
        this.noticeAudioContext = ctx
        if (ctx.state === 'suspended') {
          const resumed = ctx.resume()
          if (resumed && resumed.catch) resumed.catch(() => {})
        }
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        const now = ctx.currentTime
        osc.type = 'sine'
        osc.frequency.setValueAtTime(880, now)
        osc.frequency.setValueAtTime(660, now + 0.18)
        gain.gain.setValueAtTime(0.0001, now)
        gain.gain.exponentialRampToValueAtTime(0.18, now + 0.03)
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(now)
        osc.stop(now + 0.45)
      } catch (e) {
        // Browser audio may be blocked until the user interacts with the page.
      }
    },
    speakAlarmNotice(delta) {
      if (typeof window === 'undefined' || !window.speechSynthesis || !window.SpeechSynthesisUtterance) return
      try {
        const text = delta > 1 ? `有 ${delta} 条新的燃气告警，请及时处理` : '有新的燃气告警，请及时处理'
        const utterance = new SpeechSynthesisUtterance(text)
        utterance.lang = 'zh-CN'
        utterance.rate = 1
        utterance.volume = 1
        window.speechSynthesis.cancel()
        window.speechSynthesis.speak(utterance)
      } catch (e) {
        // Speech synthesis support varies by browser.
      }
    },
    logout() {
      this.$store.commit('CLEAR_AUTH')
      resetRouter()
      this.$router.push('/')
    }
  }
}
</script>

<style scoped>
.gas-layout {
  height: 100vh;
  background: var(--gas-layout-bg, #f5f7fa);
}
.gas-aside {
  background: linear-gradient(180deg, var(--gas-sidebar-from) 0%, var(--gas-sidebar-to) 100%);
  border-right: none;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.12);
  overflow-y: auto;
}
.gas-menu {
  padding: 12px 0 24px;
}
.menu-item,
.submenu-item {
  display: flex;
  align-items: center;
  color: var(--gas-sidebar-text);
  text-decoration: none;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, color 0.15s;
}
.menu-item {
  height: 44px;
  padding: 0 20px;
  font-size: 14px;
}
.menu-item:hover,
.submenu-item:hover {
  color: var(--gas-sidebar-active-text);
  background: rgba(255, 255, 255, 0.08);
}
.menu-item i:first-child {
  margin-right: 10px;
  font-size: 16px;
}
.menu-parent .arrow {
  margin-left: auto;
  font-size: 12px;
  transition: transform 0.2s;
  opacity: 0.75;
}
.menu-parent.expanded .arrow {
  transform: rotate(180deg);
}
.menu-parent.active {
  color: var(--gas-sidebar-active-text);
}
.menu-item.active,
.submenu-item.active {
  color: var(--gas-sidebar-active-text);
  background: var(--gas-sidebar-active-bg);
  font-weight: 500;
}
.submenu-item {
  height: 40px;
  padding-left: 46px;
  font-size: 13px;
}
.submenu-icon {
  display: inline-block;
  width: 14px;
  flex: 0 0 14px;
  margin-right: 8px;
  font-size: 14px;
  text-align: center;
}
.submenu-item.active {
  border-left: 3px solid var(--gas-primary);
  padding-left: 43px;
}
.gas-header {
  background: linear-gradient(90deg, var(--gas-header-from) 0%, var(--gas-header-to) 72%);
  border-bottom: 2px solid var(--gas-primary);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  padding: 0 24px;
}
.brand {
  display: flex;
  align-items: center;
  min-width: 280px;
}
.brand-link {
  cursor: pointer;
  user-select: none;
  border-radius: 4px;
  padding: 4px 8px 4px 0;
  margin: -4px 0;
  transition: background 0.15s;
}
.brand-link:hover .brand-name {
  color: var(--gas-primary);
}
.brand-link:active {
  opacity: 0.85;
}
.brand-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
  margin-right: 10px;
}
.brand-name {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
  transition: color 0.15s;
}
.header-center {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #86909c;
  font-size: 14px;
  flex-wrap: wrap;
}
.header-clock {
  white-space: nowrap;
}
.header-weather-divider {
  color: #c9cdd4;
  user-select: none;
}
.header-weather {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.weather-icon {
  font-size: 18px;
  line-height: 1;
}
.weather-label {
  color: #4e5969;
}
.weather-temp {
  color: #1d2129;
  font-weight: 600;
}
.weather-temp--muted {
  color: #86909c;
  font-weight: 400;
}
.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 280px;
  justify-content: flex-end;
}
.header-badge {
  line-height: 1;
}
.header-icon {
  font-size: 18px;
  color: #4e5969;
  cursor: pointer;
  transition: color 0.15s;
}
.header-icon:hover {
  color: var(--gas-primary);
}
.user-block {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #1d2129;
}
.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--gas-primary);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}
.gas-main {
  padding: 20px 24px;
  overflow: auto;
}
</style>
